import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inquiriesAPI, partsAPI, productTypesAPI, standardsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toProperCase, PROPER_CASE_FIELDS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Package, Eye, History, Upload, FileSpreadsheet, Download, Loader2, CheckCircle, AlertCircle, Boxes, Trash2, Copy, Edit2, Cable, MessageSquarePlus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { PaginationControls } from '../components/PaginationControls';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/bulk/BulkActionBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PartHistoryTab from '../components/parts/PartHistoryTab';
import WireCostingDialog from '../components/costings/WireCostingDialog';
import { WirePartForm } from './wire/WirePartsPage';

const PART_CSV_COLUMNS = [
  { header: 'Internal Part No', value: (row) => row.internal_part_no || row.internal_part_code },
  { header: 'Customer P/N', value: (row) => row.customer_part_no },
  { header: 'Description', value: (row) => row.description },
  { header: 'Type', value: (row) => row.part_type },
  { header: 'Standard', value: (row) => row.standard },
  { header: 'Grade', value: (row) => row.grade },
  { header: 'Finish', value: (row) => row.finish },
  { header: 'Weight (g)', value: (row) => row.net_weight_grams },
  { header: 'Customer', value: (row) => row.customer_name },
];

export default function PartsPage() {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [partTypeFilter, setPartTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [productTypes, setProductTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [limit, setLimit] = useState(50);
  const selection = useBulkSelection(parts);

  const canImport = user?.role === 'admin' || user?.role === 'manager';

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { is_active: activeFilter === 'inactive' ? false : true, page, limit };
      if (search) params.search = search;
      if (partTypeFilter) params.part_type = partTypeFilter;
      const response = await partsAPI.list(params);
      const items = response.data.items || response.data.data || [];
      setParts(items);
      selection.remember(items);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch parts');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, partTypeFilter, limit, activeFilter]);

  useEffect(() => {
    productTypesAPI.list().then(res => setProductTypes(res.data));
  }, []);

  useEffect(() => {
    setPage(1); // Reset to page 1 when filters change
  }, [search, partTypeFilter, limit, activeFilter]);

  useEffect(() => {
    fetchParts();
  }, [fetchParts]);

  const handleCreate = async (data) => {
    try {
      await partsAPI.create(data);
      toast.success('Part created successfully');
      setDialogOpen(false);
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Creation failed');
    }
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    fetchParts();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
            Parts Master
          </h1>
          <p className="text-muted-foreground">Searchable database of all costed parts</p>
        </div>
        <div className="flex gap-2">
          {canImport && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-parts-btn">
                  <Upload className="mr-2 h-4 w-4" />
                  Import from Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase">Import Parts from Excel</DialogTitle>
                  <DialogDescription>
                    Upload an Excel file to bulk import parts into the Parts Master
                  </DialogDescription>
                </DialogHeader>
                <ExcelImportForm onSuccess={handleImportSuccess} onCancel={() => setImportDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-part-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Part
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Add New Part</DialogTitle>
                <DialogDescription>
                  Register a new part in the Parts Master with its specification and weight.
                </DialogDescription>
              </DialogHeader>
              <PartForm onSubmit={handleCreate} onCancel={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number, description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="part-search-input"
              />
            </div>
            <Select value={partTypeFilter || 'all'} onValueChange={(v) => setPartTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="part-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {productTypes.map((t) => (
                  <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger className="w-[150px]" data-testid="part-active-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active parts</SelectItem>
                <SelectItem value="inactive">Inactive parts</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline" className="whitespace-nowrap">
              {total} parts found
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActionBar
            entity="parts"
            selectedIds={selection.selectedIds}
            selectedRows={selection.selectedRows}
            onClear={selection.clear}
            onDone={fetchParts}
            csvColumns={PART_CSV_COLUMNS}
            csvPrefix="parts"
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : parts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No parts found</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.allOnPageSelected}
                        onCheckedChange={selection.togglePage}
                        aria-label="Select all parts on this page"
                        data-testid="parts-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Internal Code</TableHead>
                    <TableHead>Customer P/N</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Standard</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Weight (g)</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parts.map((part) => (
                    <TableRow key={part.id} className="table-row" data-state={selection.isSelected(part.id) ? 'selected' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selection.isSelected(part.id)}
                          onCheckedChange={() => selection.toggleOne(part)}
                          aria-label={`Select part ${part.internal_part_no}`}
                          data-testid={`parts-row-checkbox-${part.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-medium">{part.internal_part_no || part.internal_part_code}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {part.customer_part_no || part.customer_part_number || '-'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{part.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{part.part_type}</Badge>
                      </TableCell>
                      <TableCell>{part.standard || '-'}</TableCell>
                      <TableCell>{part.grade || '-'}</TableCell>
                      <TableCell className="font-mono">{part.net_weight_grams || '-'}</TableCell>
                      <TableCell>{part.customer_name || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/parts/${part.id}`)}
                            data-testid={`view-part-${part.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/parts/${part.id}?tab=history`)}
                            title="View history"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onLimitChange={setLimit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// Excel Import Form Component
function ExcelImportForm({ onSuccess, onCancel }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const response = await partsAPI.importExcel(file);
      setResult(response.data);
      toast.success(response.data.message);
      if (response.data.created > 0) {
        onSuccess();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import file';
      toast.error(errorMsg);
      setResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a sample Excel template
    const headers = ['PartNo', 'Description', 'Grade', 'NetWeight', 'Type', 'Standard', 'Finish', 'CustomerPartNo'];
    const sampleData = [
      ['STF-BLT-001', 'M10x50 Hex Bolt', '8.8', '35.5', 'Bolt', 'ISO', 'Zinc Plated', 'CUST-001'],
      ['STF-NUT-001', 'M10 Hex Nut', '8', '12.3', 'Nut', 'DIN', 'Plain', 'CUST-002'],
    ];
    
    // Create CSV content (simple template)
    const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'parts_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded - open in Excel and save as .xlsx');
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          Excel File Format
        </h4>
        <p className="text-sm text-muted-foreground mb-2">
          Your Excel file should have the following columns:
        </p>
        <ul className="text-xs text-muted-foreground space-y-1 ml-4 list-disc">
          <li><strong>PartNo</strong> (required) - Internal part number</li>
          <li><strong>Description</strong> (required) - Part description</li>
          <li><strong>Grade</strong> - Material grade (e.g., 8.8, 10.9)</li>
          <li><strong>NetWeight</strong> - Weight in grams</li>
          <li><strong>Type</strong> - Part type (Bolt, Nut, Screw, etc.)</li>
          <li><strong>Standard</strong> - ISO, DIN, ASTM, etc.</li>
          <li><strong>Finish</strong> - Surface finish</li>
          <li><strong>CustomerPartNo</strong> - Customer&apos;s part number</li>
        </ul>
        <Button 
          variant="link" 
          size="sm" 
          className="mt-2 p-0 h-auto text-primary"
          onClick={downloadTemplate}
        >
          <Download className="h-3 w-3 mr-1" />
          Download Template
        </Button>
      </div>

      {/* File Input */}
      <div className="space-y-2">
        <Label>Select Excel File</Label>
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            file ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-muted/30'
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
            data-testid="excel-file-input"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileSpreadsheet className="h-8 w-8 text-primary" />
              <div className="text-left">
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to select or drag and drop
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                .xlsx or .xls files only
              </p>
            </>
          )}
        </div>
      </div>

      {/* Result Display */}
      {result && !result.error && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div>
              <p className="font-medium text-emerald-800">Import Successful</p>
              <p className="text-sm text-emerald-700">
                {result.created} parts created, {result.skipped} skipped (duplicates)
              </p>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-xs text-amber-700">
                  <p className="font-medium">Warnings:</p>
                  <ul className="list-disc ml-4">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <li key={`err-${err.substring(0, 30)}-${i}`}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {result?.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Import Failed</p>
              <p className="text-sm text-red-700">{result.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          className="btn-primary" 
          onClick={handleUpload} 
          disabled={!file || uploading}
          data-testid="upload-excel-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Import Parts
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}

function PartForm({ onSubmit, onCancel, editPart = null }) {
  const [productTypes, setProductTypes] = useState([]);
  const [standards, setStandards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [formData, setFormData] = useState({
    internal_part_no: editPart?.internal_part_no || '',
    customer_part_no: editPart?.customer_part_no || '',
    part_type: editPart?.part_type || '',
    standard: editPart?.standard || '',
    description: editPart?.description || '',
    grade: editPart?.grade || '',
    finish: editPart?.finish || '',
    net_weight_grams: editPart?.net_weight_grams || '',
    hs_code: editPart?.hs_code || '',
    packing_qty: editPart?.packing_qty || '',
    box_size: editPart?.box_size || '',
    part_category: editPart?.part_category || 'component',
    manufacturing_type: editPart?.manufacturing_type || 'in_house',
    product_category: editPart?.product_category || 'fastener',
    customer_id: editPart?.customer_id || '',
    customer_name: editPart?.customer_name || '',
    // Wire-specific (only relevant when product_category === 'wire')
    wire_diameter_min_mm: editPart?.wire_diameter_min_mm || '',
    wire_diameter_max_mm: editPart?.wire_diameter_max_mm || '',
    wire_grade: editPart?.wire_grade || '',
    wire_coil_weight_kg: editPart?.wire_coil_weight_kg || '',
    wire_uts_min_mpa: editPart?.wire_uts_min_mpa || '',
    wire_uts_max_mpa: editPart?.wire_uts_max_mpa || '',
    wire_hardness_notes: editPart?.wire_hardness_notes || '',
  });

  useEffect(() => {
    Promise.all([productTypesAPI.list(), standardsAPI.list()]).then(([types, stds]) => {
      setProductTypes(types.data);
      setStandards(stds.data);
    });
    // Fetch customers
    import('../services/api').then(({ customersAPI }) => {
      customersAPI.list().then(res => setCustomers(res.data.customers || res.data || []));
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlurProperCase = (e) => {
    const { name, value } = e.target;
    if (PROPER_CASE_FIELDS.has(name) && value) {
      setFormData(prev => ({ ...prev, [name]: toProperCase(value) }));
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name
    }));
    setCustomerSearch('');
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // Guard: if the user typed into the customer search but didn't pick a
    // suggestion, formData still holds the old customer (or is blank) — this
    // silently swallowed the change. Force them to pick from the dropdown.
    if (customerSearch.trim() && !formData.customer_id) {
      toast.error('Please select a customer from the dropdown, or clear the search box.');
      return;
    }
    const NUMERIC_FIELDS = new Set([
      'net_weight_grams', 'wire_diameter_min_mm', 'wire_diameter_max_mm',
      'wire_coil_weight_kg', 'wire_uts_min_mpa', 'wire_uts_max_mpa', 'packing_qty',
    ]);
    // Fields where an empty value means "explicitly clear it on the server".
    // We send `null` for these so the backend can unset them (see
    // parts.py::update_part — nullable-clearable list must match).
    const NULLABLE_CLEARABLE = new Set(['customer_id', 'customer_name']);
    const submitData = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        submitData[key] = NUMERIC_FIELDS.has(key) ? parseFloat(value) : value;
      } else if (NULLABLE_CLEARABLE.has(key) && editPart) {
        // Only clear on edit (not on create) so we don't spam nulls at create time
        submitData[key] = null;
      }
    });
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* NEW: Part Category & Manufacturing Type */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="space-y-2">
          <Label>Part Category *</Label>
          <Select
            value={formData.part_category}
            onValueChange={(value) => setFormData(prev => ({ ...prev, part_category: value }))}
          >
            <SelectTrigger data-testid="part-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="component">Component (Single Part)</SelectItem>
              <SelectItem value="kit">Kit (Assembly of Parts)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {formData.part_category === 'kit' 
              ? 'Kit = Assembly of multiple child parts (BOM required)'
              : 'Component = Standard part with process flow'}
          </p>
        </div>
        <div className="space-y-2">
          <Label>Manufacturing Type *</Label>
          <Select
            value={formData.manufacturing_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, manufacturing_type: value }))}
          >
            <SelectTrigger data-testid="manufacturing-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in_house">In-House</SelectItem>
              <SelectItem value="outsourced">Outsourced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Internal Part Code *</Label>
          <Input
            name="internal_part_no"
            value={formData.internal_part_no}
            onChange={handleChange}
            required
            data-testid="part-code-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Customer Part Number</Label>
          <Input
            name="customer_part_no"
            value={formData.customer_part_no}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Part Type *</Label>
          <Select
            value={formData.part_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, part_type: value }))}
          >
            <SelectTrigger data-testid="part-type-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {productTypes.map((t) => (
                <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Standard</Label>
          <Select
            value={formData.standard}
            onValueChange={(value) => setFormData(prev => ({ ...prev, standard: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select standard" />
            </SelectTrigger>
            <SelectContent>
              {standards.map((s) => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description *</Label>
        <Input
          name="description"
          value={formData.description}
          onChange={handleChange}
          onBlur={handleBlurProperCase}
          required
          data-testid="part-description-input"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Grade</Label>
          <Input
            name="grade"
            value={formData.grade}
            onChange={handleChange}
            placeholder="e.g., 8.8"
          />
        </div>
        <div className="space-y-2">
          <Label>Finish</Label>
          <Input
            name="finish"
            value={formData.finish}
            onChange={handleChange}
            onBlur={handleBlurProperCase}
            placeholder="e.g., Zinc Plated"
          />
        </div>
        <div className="space-y-2">
          <Label>Net Weight (g)</Label>
          <Input
            name="net_weight_grams"
            type="number"
            step="0.01"
            value={formData.net_weight_grams}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>HS Code (Harmonized System Code)</Label>
        <Input
          name="hs_code"
          value={formData.hs_code}
          onChange={handleChange}
          placeholder="e.g., 7318.15.00"
          data-testid="hs-code-input"
        />
        <p className="text-xs text-muted-foreground">8-10 digit customs classification code for export/import</p>
      </div>

      {/* Packing */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Packing Qty (pcs/box)</Label>
          <Input
            name="packing_qty"
            type="number"
            step="1"
            min="0"
            value={formData.packing_qty}
            onChange={handleChange}
            placeholder="e.g., 500"
            data-testid="packing-qty-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Box Size</Label>
          <Input
            name="box_size"
            value={formData.box_size}
            onChange={handleChange}
            placeholder="e.g., 40x30x25 cm"
            data-testid="box-size-input"
          />
        </div>
      </div>

      {/* Customer Selection */}
      <div className="space-y-2">
        <Label>Customer</Label>
        <div className="relative">
          {formData.customer_name ? (
            <div className="flex items-center justify-between p-2 border rounded-md bg-muted/30">
              <span className="font-medium">{formData.customer_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, customer_id: '', customer_name: '' }))}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="pl-9"
                data-testid="customer-search-input"
              />
              {customerSearch && filteredCustomers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCustomers.slice(0, 10).map((c) => (
                    <div
                      key={c.id}
                      className="p-2 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                      onClick={() => handleCustomerSelect(c)}
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.customer_code}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Link this part to a specific customer (optional)</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="btn-primary" data-testid="save-part-btn">
          {editPart ? 'Update Part' : 'Create Part'}
        </Button>
      </div>
    </form>
  );
}

// Part Detail Page
export function PartDetailPage() {
  const [part, setPart] = useState(null);
  const [history, setHistory] = useState(null);
  const [summary, setSummary] = useState(null);
  const [creatingInquiry, setCreatingInquiry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [wireCostingOpen, setWireCostingOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const partId = window.location.pathname.split('/')[2];
  const navigate = useNavigate();

  useEffect(() => {
    fetchPart();
  }, [partId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPart = async () => {
    try {
      const response = await partsAPI.get(partId);
      setPart(response.data.part);
      setHistory(response.data.history);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error('Failed to fetch part');
      navigate('/parts');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data) => {
    try {
      await partsAPI.update(partId, data);
      toast.success('Part updated successfully');
      setEditDialogOpen(false);
      fetchPart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update part');
    }
  };

  const handleDelete = async () => {
    try {
      await partsAPI.delete(partId);
      toast.success('Part deactivated successfully');
      navigate('/parts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete part');
    }
  };

  const handleNewInquiry = async () => {
    setCreatingInquiry(true);
    try {
      const response = await inquiriesAPI.create({
        customer_id: part.customer_id || undefined,
        customer_name: part.customer_name || undefined,
        products: [{
          part_master_id: part.id,
          description: part.description,
          quantity: 1,
          quantity_unit: 'pcs',
        }],
        remarks: `Repeat inquiry for ${part.internal_part_no}`,
      });
      toast.success(`Inquiry ${response.data.inquiry_number} created`, {
        description: 'Attach the customer document and run AI extraction inside the inquiry.',
      });
      navigate(`/inquiries/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the inquiry');
    } finally {
      setCreatingInquiry(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await partsAPI.duplicate(partId);
      toast.success(`Part duplicated: ${res.data.internal_part_no}`);
      navigate(`/parts/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate part');
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!part) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
            {part.internal_part_no || part.internal_part_code}
            {part.product_category === 'wire' && (
              <Badge className="ml-3 align-middle bg-blue-100 text-blue-800 hover:bg-blue-200" data-testid="wire-badge">
                <Cable className="h-3 w-3 mr-1" />Wire
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">{part.description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Edit Button — wire-aware (renders WirePartForm for wire parts) */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-part-btn">
                <Edit2 className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit {part.product_category === 'wire' ? 'Wire ' : ''}Part</DialogTitle>
                <DialogDescription>Update part details</DialogDescription>
              </DialogHeader>
              {part.product_category === 'wire' ? (
                <WirePartForm
                  editPart={part}
                  onSubmit={handleUpdate}
                  onCancel={() => setEditDialogOpen(false)}
                />
              ) : (
                <PartForm
                  onSubmit={handleUpdate}
                  onCancel={() => setEditDialogOpen(false)}
                  editPart={part}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* New Inquiry for this part */}
          <Button variant="outline" onClick={handleNewInquiry} disabled={creatingInquiry} data-testid="new-inquiry-from-part-btn">
            {creatingInquiry ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquarePlus className="mr-2 h-4 w-4" />}
            New Inquiry
          </Button>

          {/* Duplicate Button */}
          <Button 
            variant="outline"
            onClick={handleDuplicate}
            disabled={duplicating}
            data-testid="duplicate-part-btn"
          >
            {duplicating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Copy className="mr-2 h-4 w-4" />
            )}
            Duplicate
          </Button>

          {/* BOM Builder button for Kit parts */}
          {part.part_category === 'kit' && (
            <Button 
              variant="outline"
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              onClick={() => navigate(`/bom/new?part=${part.id}`)}
              data-testid="bom-builder-btn"
            >
              <Boxes className="mr-2 h-4 w-4" />
              Bill of Materials
            </Button>
          )}

          {/* Re-Cost / New Wire Costing Button — context-aware */}
          {part.product_category === 'wire' ? (
            <Button
              className="btn-primary"
              onClick={() => setWireCostingOpen(true)}
              data-testid="new-wire-costing-btn"
            >
              <Cable className="mr-2 h-4 w-4" />
              New Wire Costing
            </Button>
          ) : (
            <Button
              className="btn-primary"
              onClick={() => navigate(`/costings/new?part=${part.id}`)}
            >
              Re-Cost This Part
            </Button>
          )}

          {/* Delete Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="delete-part-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Part</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>{part.internal_part_no}</strong>?
                  <br /><br />
                  This will deactivate the part. Parts linked to quotations cannot be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-part-btn"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Part Details + History (Tabs) */}
      <Tabs defaultValue="overview" className="w-full" data-testid="part-detail-tabs">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            360° History
            <Badge variant="outline" className="ml-2 text-[10px]">
              {(history?.costings?.length || 0) + (history?.quotations?.length || 0) + (history?.orders?.length || 0)}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-6">
          {/* Part Details */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Part Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Part Type</p>
                  <Badge variant="outline">{part.part_type}</Badge>
                </div>

                {part.product_category === 'wire' ? (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Diameter</p>
                      <p className="font-mono">
                        {part.wire_diameter_min_mm || '-'}{' - '}{part.wire_diameter_max_mm || '-'} mm
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Wire Grade</p>
                      <p className="font-medium">{part.wire_grade || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Coil Weight</p>
                      <p className="font-mono">{part.wire_coil_weight_kg ? `${part.wire_coil_weight_kg} kg` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">UTS Range</p>
                      <p className="font-mono">
                        {part.wire_uts_min_mpa || '-'}{' - '}{part.wire_uts_max_mpa || '-'} MPa
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Hardness / Notes</p>
                      <p className="font-medium text-sm">{part.wire_hardness_notes || '-'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Standard</p>
                      <p className="font-medium">{part.standard || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Grade</p>
                      <p className="font-medium">{part.grade || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Finish</p>
                      <p className="font-medium">{part.finish || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Net Weight</p>
                      <p className="font-mono">{part.net_weight_grams || '-'} g</p>
                    </div>
                  </>
                )}

                <div>
                  <p className="text-sm text-muted-foreground">HS Code</p>
                  <p className="font-mono">{part.hs_code || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Packing Qty</p>
                  <p className="font-mono">{part.packing_qty ? `${part.packing_qty} pcs/box` : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Box Size</p>
                  <p className="font-medium">{part.box_size || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  {part.customer_id ? (
                    <button
                      className="font-medium text-blue-700 hover:underline"
                      onClick={() => navigate(`/customers/${part.customer_id}`)}
                      data-testid="link-to-customer"
                    >
                      {part.customer_name || '-'}
                    </button>
                  ) : (
                    <p className="font-medium">{part.customer_name || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Customer P/N</p>
                  <p className="font-mono">{part.customer_part_no || part.customer_part_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={part.is_active ? 'badge-success' : 'badge-error'}>
                    {part.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick-glance counts (legacy 3-card view, kept for at-a-glance) */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="py-4 px-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Costings</p>
              <p className="text-3xl font-bold font-mono text-blue-600 mt-1">{history?.costings?.length || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="py-4 px-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quotations</p>
              <p className="text-3xl font-bold font-mono text-amber-600 mt-1">{history?.quotations?.length || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="py-4 px-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sales Orders</p>
              <p className="text-3xl font-bold font-mono text-emerald-600 mt-1">{history?.orders?.length || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="py-4 px-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Process Flows</p>
              <p className="text-3xl font-bold font-mono text-purple-600 mt-1">{history?.process_flows?.length || 0}</p>
            </CardContent></Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          {summary && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="part-history-summary">
              <Card className="card-dashboard"><CardContent className="pt-5">
                <p className="text-xs uppercase text-muted-foreground">Inquiries</p>
                <p className="text-2xl font-heading font-bold" data-testid="part-summary-inquiries">{summary.inquiry_count}</p>
                <p className="text-xs text-muted-foreground">{summary.costing_count} costing version(s)</p>
              </CardContent></Card>
              <Card className="card-dashboard"><CardContent className="pt-5">
                <p className="text-xs uppercase text-muted-foreground">Quotations</p>
                <p className="text-2xl font-heading font-bold" data-testid="part-summary-quotations">{summary.quotation_count}</p>
                <p className="text-xs text-muted-foreground">{summary.quotations_won} won · {summary.quotations_lost} lost</p>
              </CardContent></Card>
              <Card className="card-dashboard"><CardContent className="pt-5">
                <p className="text-xs uppercase text-muted-foreground">Total ordered qty</p>
                <p className="text-2xl font-heading font-bold" data-testid="part-summary-ordered-qty">{Number(summary.total_ordered_quantity || 0).toLocaleString('en-IN')}</p>
                <p className="text-xs text-muted-foreground">{summary.order_count} order(s)</p>
              </CardContent></Card>
              <Card className="card-dashboard"><CardContent className="pt-5">
                <p className="text-xs uppercase text-muted-foreground">Last order</p>
                <p className="text-lg font-heading font-bold" data-testid="part-summary-last-order">{summary.last_order_number || '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {summary.last_order_date ? new Date(summary.last_order_date).toLocaleDateString('en-IN') : 'No orders yet'}
                  {summary.last_quoted_price ? ` · last quoted ${summary.last_quoted_currency} ${Number(summary.last_quoted_price).toFixed(2)}` : ''}
                </p>
              </CardContent></Card>
            </div>
          )}
          <PartHistoryTab partId={part.id} history={history} currency="INR" onReload={fetchPart} />
        </TabsContent>
      </Tabs>

      {/* Wire Costing Dialog — only mounted when part is wire */}
      {part.product_category === 'wire' && (
        <WireCostingDialog
          open={wireCostingOpen}
          onOpenChange={setWireCostingOpen}
          part={part}
          onCreated={fetchPart}
        />
      )}
    </div>
  );
}
